/**
 * Matches a 3–5 digit local extension.
 *
 * This is the frontend mirror of the FreeSWITCH `Local_Extension` dialplan
 * regex (`^(\d{3,5})$`) built by
 * `DialplanConfigurationHandler.BuildDefaultLocalExtension` on the backend.
 * That dialplan is what classifies a call as internal (it exports
 * `cx_internal_call=true` and the `X-CX-Internal-Call` SIP header for these
 * destinations). Keep this pattern in sync with the dialplan — if the backend
 * extension range changes, this must change too, otherwise the frontend and
 * backend will disagree on what counts as internal.
 */
const LOCAL_EXTENSION_PATTERN = /^\d{3,5}$/;

/**
 * Whether the given dialer value targets an internal (agent-to-agent)
 * extension.
 *
 * The value is trimmed and stripped of cosmetic separators (spaces, dashes,
 * dots, parentheses) before being tested against {@link LOCAL_EXTENSION_PATTERN}
 * (`^\d{3,5}$`). This mirrors the FreeSWITCH `Local_Extension` dialplan so that
 * an outbound call the frontend flags as internal is the same call the backend
 * classifies as internal. External numbers (with a `+`/`00` prefix or more than
 * five digits) are therefore rejected.
 *
 * Examples:
 *  - `"1001"` → true
 *  - `"10 01"` → true
 *  - `"4000"` → true
 *  - `"00905321234567"` → false
 *  - `"+90 532 123 45 67"` → false
 *  - `"0"` → false
 *  - `"test"` → false
 */
export function isInternalExtension(value: string): boolean {
  const normalized = value.trim().replace(/[\s\-().]/g, '');
  return LOCAL_EXTENSION_PATTERN.test(normalized);
}
